import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  type ReactNode,
} from 'react';
import type { BusinessVisibility } from '@/types';
import {
  validatePartnerToken,
  getStoredPartnerSession,
  storePartnerSession,
  clearPartnerSession,
  isSessionExpired,
  autoRefreshSession,
  type PartnerSessionData,
} from '@/services/partnerAuth';

// ===========================================
// Types
// ===========================================

interface PartnerAuthContextType {
  // State
  isAuthenticated: boolean;
  isLoading: boolean;
  session: PartnerSessionData | null;
  error: string | null;

  // Getters
  companyId: string | null;
  companyName: string | null;
  businessVisibility: BusinessVisibility | null;
  sessionToken: string | null;

  // Actions
  loginWithToken: (token: string) => Promise<boolean>;
  logout: () => void;
  refreshSession: () => Promise<boolean>;
}

// ===========================================
// Context
// ===========================================

const PartnerAuthContext = createContext<PartnerAuthContextType | undefined>(undefined);

// ===========================================
// Provider
// ===========================================

interface PartnerAuthProviderProps {
  children: ReactNode;
}

export function PartnerAuthProvider({ children }: PartnerAuthProviderProps) {
  const [session, setSession] = useState<PartnerSessionData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Derived state
  const isAuthenticated = session !== null && !isSessionExpired(session);

  // Load session from storage on mount
  useEffect(() => {
    const storedSession = getStoredPartnerSession();

    if (storedSession && !isSessionExpired(storedSession)) {
      setSession(storedSession);
    } else if (storedSession) {
      // Session expired, clear it
      clearPartnerSession();
    }

    setIsLoading(false);
  }, []);

  // Auto-refresh session periodically
  useEffect(() => {
    if (!session) return;

    const refreshInterval = setInterval(async () => {
      const refreshedSession = await autoRefreshSession(session);
      if (refreshedSession) {
        setSession(refreshedSession);
      } else {
        // Refresh failed, session expired
        setSession(null);
        clearPartnerSession();
        setError('Session expired. Please log in again.');
      }
    }, 5 * 60 * 1000); // Check every 5 minutes

    return () => clearInterval(refreshInterval);
  }, [session]);

  // Login with magic link token
  const loginWithToken = useCallback(async (token: string): Promise<boolean> => {
    setIsLoading(true);
    setError(null);

    try {
      const result = await validatePartnerToken(token);

      if (!result.valid || !result.sessionToken) {
        setError(result.error || 'Failed to validate token');
        setIsLoading(false);
        return false;
      }

      const newSession: PartnerSessionData = {
        sessionToken: result.sessionToken,
        companyId: result.companyId!,
        companyName: result.companyName!,
        businessVisibility: result.businessVisibility!,
        expiresAt: new Date(result.expiresAt!),
      };

      storePartnerSession(newSession);
      setSession(newSession);
      setIsLoading(false);
      return true;
    } catch (err) {
      console.error('Login error:', err);
      setError('An error occurred during login');
      setIsLoading(false);
      return false;
    }
  }, []);

  // Logout
  const logout = useCallback(() => {
    clearPartnerSession();
    setSession(null);
    setError(null);
  }, []);

  // Manual refresh session
  const refreshSession = useCallback(async (): Promise<boolean> => {
    if (!session) return false;

    const refreshedSession = await autoRefreshSession(session);
    if (refreshedSession) {
      setSession(refreshedSession);
      return true;
    }

    // Refresh failed
    logout();
    return false;
  }, [session, logout]);

  // Context value
  const value: PartnerAuthContextType = {
    isAuthenticated,
    isLoading,
    session,
    error,
    companyId: session?.companyId || null,
    companyName: session?.companyName || null,
    businessVisibility: session?.businessVisibility || null,
    sessionToken: session?.sessionToken || null,
    loginWithToken,
    logout,
    refreshSession,
  };

  return (
    <PartnerAuthContext.Provider value={value}>
      {children}
    </PartnerAuthContext.Provider>
  );
}

// ===========================================
// Hook
// ===========================================

export function usePartnerAuth(): PartnerAuthContextType {
  const context = useContext(PartnerAuthContext);
  if (!context) {
    throw new Error('usePartnerAuth must be used within a PartnerAuthProvider');
  }
  return context;
}
